const FALLBACK_ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Paris",
  "Asia/Kolkata",
  "Asia/Jerusalem",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "Pacific/Kiritimati",
] as const;

const TIME_ZONE_ALIASES: Readonly<Record<string, string>> = {
  "asia/calcutta": "Asia/Kolkata",
  eire: "Europe/Dublin",
  israel: "Asia/Jerusalem",
};

export function normalizeTimeZoneId(timeZoneId: string): string {
  return TIME_ZONE_ALIASES[timeZoneId.toLowerCase()] ?? timeZoneId;
}

export function supportedTimeZones(): string[] {
  const supportedValuesOf = (
    Intl as typeof Intl & {
      supportedValuesOf?: (key: "timeZone") => string[];
    }
  ).supportedValuesOf;
  const zones = (supportedValuesOf?.("timeZone") ?? [...FALLBACK_ZONES])
    .map(normalizeTimeZoneId);
  return Array.from(new Set(["UTC", ...FALLBACK_ZONES, ...zones])).sort(
    (left, right) => left.localeCompare(right),
  );
}

export function browserTimeZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const normalized = normalizeTimeZoneId(zone ?? "");
  return normalized && isValidTimeZone(normalized) ? normalized : "UTC";
}

export function isValidTimeZone(timeZoneId: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timeZoneId }).format();
    return true;
  } catch {
    return false;
  }
}

export function dateKeyInTimeZone(
  instant: Date,
  timeZoneId: string | null | undefined,
): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: isValidTimeZone(timeZoneId ?? "") ? timeZoneId! : "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const value = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

export function hourInTimeZone(
  instant: Date,
  timeZoneId: string | null | undefined,
): number {
  const hour = new Intl.DateTimeFormat("en-US", {
    timeZone: isValidTimeZone(timeZoneId ?? "") ? timeZoneId! : "UTC",
    hour: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(instant)
    .find(part => part.type === "hour")?.value;
  return Number(hour ?? 0);
}

export function formatDateTimeInTimeZone(
  value: string,
  timeZoneId: string,
): string {
  const effectiveZone = isValidTimeZone(timeZoneId) ? timeZoneId : "UTC";
  return new Intl.DateTimeFormat(undefined, {
    timeZone: effectiveZone,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
}

export function timeZoneLabel(timeZoneId: string): string {
  return timeZoneId === "UTC"
    ? "UTC"
    : timeZoneId.replaceAll("_", " ").replace("/", " — ");
}
